define((require) => {
    "use strict";

    const _                   = require("underscore");
    const Marionette          = require("marionette");
    const Browser             = require("shared/services/Browser");
    const EffectModel         = require("shared/ui/experience/action/effects/EffectModel");
    const AnimationController = require("shared/ui/experience/widgets/animations/AnimationController");
    const Effects             = require("shared/ui/experience/widgets/effects/Effects");
    const _throttle           = require("lodash/throttle");

    const STATE = {
        Hover: "hover",
        Attention: "attention"
    };

    /**
     * Responsible for managing/controlling the effects associated with a widget view.
     *
     * Handles the <tt>mouseover</tt> and <tt>mouseout</tt> events, playing any needed effects.
     *
     * Only a single hover and/or a single attention effect is supported for now.
     *
     * @class EffectController
     */
    const EffectController = Marionette.Controller.extend({

        _active: false,

        _state: STATE.Attention,

        _mouseMoveEventHandler: null,
        /**
         * @type {AbstractWidgetView}
         */
        _widgetView: null,


        /**
         * @type {jQueryElement}
         */
        _element: null,

        _hoverEffect: null,

        _attentionEffect: null,

        _animationControllers: null,

        /**
         * Constructor.
         *
         * @param  {AbstractWidgetView} widgetView The associated widget view.
         *
         * @param  {AnimationController[]} animationControllers Array of possible associated animation
         *                                                      controllers - hover effects are not allowed while
         *                                                      transitions are considered active.
         */
        constructor(widgetView, animationControllers) {
            this._widgetView = widgetView;
            this._element = widgetView.$el;

            const effectCollection = this._widgetView.model.get("effects");

            this._createEffects(effectCollection);
            this._animationControllers = animationControllers || [];

            _.bindAll(this, "_mouseOverHandler", "_mouseOutHandler");
            // Listen for hover related events if needed
            if (effectCollection.hasTriggerType(EffectModel.TRIGGER_TYPE_HOVER) &&
                Browser.capabilities.isMouseEventsSupported) {
                this._mouseMoveEventHandler = document.getElementsByClassName("experience-viewer")[0].addEventListener("mousemove", _throttle((e) => this._handleOnHover(e)));
            }
        },

        _handleOnHover(e) {
            const widgetCoords = this._element[0].getBoundingClientRect();
            const {clientX, clientY} = e;
            const {
                x, y, width, height
            } = widgetCoords;
            if (clientX >= x && clientX <= (x + width)) {
                if (clientY >= y && clientY <= (y + height)) {
                    this._mouseOverHandler();

                    return;
                }
            }

            this._mouseOutHandler();
        },

        setActive(value) {
            if (this._active === value) {
                return;
            }

            this._active = value;

            this._updateToActiveState();
        },

        dispose() {
            if (this._mouseMoveEventHandler) {
                this._mouseMoveEventHandler
                    .off("mousemove", this._handleOnHover);
            }

            if (this._hoverEffect) {
                this._hoverEffect.kill();
            }

            if (this._attentionEffect) {
                this._attentionEffect.kill();
            }
        },

        _mouseOverHandler() {
            // Ignore the hover state if a transition is active or before an intro transition or after
            // an outro transition.
            if (this._hasActiveTransitions() || this._isPreIntroTransition() || this._isPostOutroTransition()) {
                return;
            }

            this._setState(STATE.Hover);
        },

        _mouseOutHandler() {
            this._setState(STATE.Attention);
        },

        _createEffects(effectCollection) {
            const hoverEffectModel = effectCollection.firstByTriggerType(EffectModel.TRIGGER_TYPE_HOVER);
            const attentionEffectModel = effectCollection.firstByTriggerType(EffectModel.TRIGGER_TYPE_ATTENTION);

            if (hoverEffectModel) {
                this._hoverEffect = Effects.createFromModel(this._widgetView, hoverEffectModel);
            }

            if (attentionEffectModel) {
                this._attentionEffect = Effects.createFromModel(this._widgetView, attentionEffectModel);
            }
        },

        /**
         * @private
         *
         * Whether any (widget) transitions are currently active.
         *
         * @return {Boolean} <tt>true</tt> if there's any active transitions.
         * @see  AnimationController#isActive
         */
        _hasActiveTransitions() {
            let i = this._animationControllers.length;
            let controller;

            while (i--) {
                controller = this._animationControllers[i];

                if (controller.isActive()) {
                    return true;
                }
            }

            return false;
        },

        /**
         * @private
         *
         * Whether the first transition in the list of transitions is currently in a delay state/phase
         * and is of type "intro", i.e. <tt>AnimationController.Type.INTRO</tt>.
         *
         * @return {Boolean} <tt>true</tt> if an "intro" transition is currently in a delay state/phase.
         *
         * @see  AnimationController#isRunning
         * @see  AnimationController#type
         */
        _isPreIntroTransition() {
            const introTransition = _.first(this._animationControllers);

            return introTransition && introTransition.isRunning() && introTransition.type === AnimationController.Type.INTRO && !introTransition.isActive();
        },

        /**
         * @private
         *
         * Whether the last transition in the list of transitions has completed
         * and is of type "outro", i.e. <tt>AnimationController.Type.OUTRO</tt>.
         *
         * @return {Boolean} <tt>true</tt> if an "outro" transition is has completed.
         *
         * @see  AnimationController#isComplete
         * @see  AnimationController#type
         */
        _isPostOutroTransition() {
            const outroTransition = _.last(this._animationControllers);

            return outroTransition && outroTransition.isComplete() && outroTransition.type === AnimationController.Type.OUTRO;
        },

        _setState(value) {
            if (this._state === value) {
                return;
            }

            this._state = value;

            this._updateToState();
        },

        _getEffect() {
            if (this._state === STATE.Attention) {
                return this._attentionEffect;
            }

            return this._hoverEffect;
        },

        _playEffect() {
            this._currentEffect = this._getEffect();
            if (this._currentEffect) {
                this._currentEffect.play();
            }
        },

        _updateToState() {
            if (this._currentEffect) {
                this._currentEffect.finish().done(() => {
                    this._playEffect();
                });
            } else {
                this._playEffect();
            }
        },

        _updateToActiveState() {
            if (this._active) {
                this._updateToState();
            } else {
                if (this._attentionEffect) {
                    this._attentionEffect.stop();
                }

                if (this._hoverEffect) {
                    this._hoverEffect.stop();
                }
            }
        }

    }, {
        STATE
    });

    return EffectController;
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/effects/EffectController.js